<template>
  <div v-if="overview" class="board">
    <div class="head">
      <div>
        <h1>我的看板</h1>
        <p>
          {{ partner.name || '当前主体' }}
          <template v-if="partner.inviteCode"> · 邀请码 {{ partner.inviteCode }}</template>
          · 数据范围：本级及下级 {{ downline.length }} 家
          · 统计按本月
        </p>
      </div>
      <div class="actions">
        <button v-if="pendingCount && !eco" class="pill" type="button" @click="$router.push('/partner/statements')">
          待确认结算单 {{ pendingCount }} 份
        </button>
        <button class="gold" type="button" @click="$router.push({ path: '/partner/recharge', query: { tab: 'buy' } })">购买代金券</button>
        <button class="ghost" type="button" @click="$router.push({ path: '/partner/recharge', query: { tab: 'apply' } })">申请代金券</button>
      </div>
    </div>

    <section class="invite">
      <div>
        <div class="invite-label">我的专属邀请码 · 仅面向 C 端用户</div>
        <div class="invite-row">
          <span class="discount">{{ discountShort }}</span>
          <strong>{{ partner.inviteCode || '审核通过后生成' }}</strong>
          <button v-if="partner.inviteCode" type="button" @click="copy">复制</button>
        </div>
        <p>分享给 C 端用户，注册时填写即绑定归属。由系统在账号开通时自动生成，永久有效、不可更改。</p>
        <div v-if="promoUrl" class="invite-link">
          <span>推广链接</span>
          <code>{{ promoUrl }}</code>
          <button type="button" @click="copyLink">复制</button>
        </div>
      </div>
      <aside>
        注册审核通过前不显示邀请码；账号或逾期期间邀请码暂时失效；培训未通过时优惠码不生效。该邀请码仅面向 C 端用户，渠道商之间的推荐关系是「推荐人」字段。
      </aside>
    </section>

    <section class="metrics">
      <article v-if="eco">
        <div class="k">剩余积分</div>
        <div class="v">{{ points(poolPoints) }} <small>分</small></div>
        <p>本月消耗 {{ points(monthConsumed) }} 分</p>
      </article>
      <article v-else>
        <div class="k">本月分账收益</div>
        <div class="v">{{ yuan(monthCommission) }}</div>
        <p>按每级返佣，含下级渠道产生的自充消耗</p>
      </article>
      <article>
        <div class="k">本月采购额</div>
        <div class="v">{{ yuan(monthPurchase) }}</div>
        <p>购买代金券面额合计 · 折扣 {{ discountText(overview.discount) }}</p>
      </article>
      <article>
        <div class="k">本月消耗额</div>
        <div class="v">{{ yuan(monthConsumeYuan) }}</div>
        <p>终端客户自充积分消耗，{{ monthPoints }} 分</p>
      </article>
      <article>
        <div class="k">已使用积分</div>
        <div class="v">{{ points(overview.usedSelfPoints) }} <small>分</small></div>
        <p>代金券模式，仅累计已使用积分</p>
      </article>
      <article v-if="eco">
        <div class="k">关联会员</div>
        <div class="v">{{ memberCount }} <small>人</small></div>
        <p>已关联会员 · 本月新增 {{ monthNewMembers }} 人</p>
      </article>
      <article v-else>
        <div class="k">新增客户 / 下级</div>
        <div class="v">{{ downline.length }} <small>家</small></div>
        <p>当前可见下级渠道。C 端新增客户还没单独统计</p>
      </article>
    </section>

    <section class="split">
      <article class="panel">
        <h2>经营进度</h2>
        <div v-for="row in progress" :key="row.label" class="prog">
          <div class="prog-top">
            <span>{{ row.label }}</span>
            <b>{{ row.value }}</b>
          </div>
          <div class="bar"><i :style="{ width: row.width }" /></div>
          <p>{{ row.note }}</p>
        </div>
        <div class="tip">考核目标、达标奖档位还没配置，所以这里只展示已经发生的数，不显示原型里的百分比。</div>
      </article>
      <article class="panel">
        <div class="chart-head">
          <h2>{{ eco ? '近 6 个月采购 / 消耗趋势' : '近 6 个月采购 / 消耗 / 分账趋势' }}</h2>
          <span>单位：万元</span>
        </div>
        <div ref="chart" class="chart" />
      </article>
    </section>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { getChannelLines, getChannelOverview, getChannelPartners, getChannelSettlements, getChannelVouchers, getEcoDesk } from '@/api/admin'
import { discountText, levelName } from './labels'

echarts.use([BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

export default {
  name: 'PartnerBoard',
  data() {
    return { overview: null, desk: {}, downline: [], settlements: [], vouchers: [], lines: [], chart: null }
  },
  computed: {
    partner() { return (this.overview && this.overview.partner) || {} },
    eco() { return this.partner.levelCode === 'ECO' },
    promoUrl() { return (this.overview && this.overview.promoUrl) || '' },
    poolPoints() { return Number(this.desk.points || 0) },
    monthConsumed() { return Number(this.desk.monthConsumedPoints || 0) },
    memberCount() { return Number(this.desk.memberCount || 0) },
    monthNewMembers() { return Number(this.desk.monthNewMembers || 0) },
    pendingCount() { return this.settlements.filter(item => item.status === 'PENDING_CONFIRM').length },
    discountShort() {
      const text = discountText(this.overview && this.overview.discount)
      return text === '无进货折扣' ? '—' : text.replace(' 折', '折')
    },
    monthKey() {
      const now = new Date()
      return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0')
    },
    monthLines() { return this.lines.filter(item => String(item.createdAt || '').indexOf(this.monthKey) === 0) },
    monthCommission() { return this.monthLines.reduce((sum, item) => sum + Number(item.amount || 0), 0) },
    monthPoints() { return this.monthLines.reduce((sum, item) => sum + Number(item.points || 0), 0) },
    monthConsumeYuan() { return this.monthPoints / 10 },
    monthPurchase() {
      return this.vouchers
        .filter(item => String(item.createdAt || '').indexOf(this.monthKey) === 0 && item.source === 'PURCHASE')
        .reduce((sum, item) => sum + Number(item.faceValue || 0), 0)
    },
    progress() {
      const max = Math.max(this.monthCommission, this.monthPurchase, this.monthConsumeYuan, 1)
      const rows = [
        { label: '本月分账收益', value: this.yuan(this.monthCommission), width: this.bar(this.monthCommission, max), note: '只统计自充消耗产生的每级返佣' },
        { label: '本月采购额', value: this.yuan(this.monthPurchase), width: this.bar(this.monthPurchase, max), note: '购买代金券的面额，申请和总部下发不计入' },
        { label: '本月自充消耗', value: this.points(this.monthPoints) + ' 分', width: this.bar(this.monthConsumeYuan, max), note: '1 元 = 10 积分。代金券兑换的消耗不计返佣' }
      ]
      return this.eco ? rows.filter(row => row.label !== '本月分账收益') : rows
    }
  },
  created() { this.load() },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    discountText, levelName,
    yuan(value) {
      return '¥ ' + Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    points(value) { return Number(value || 0).toLocaleString('zh-CN') },
    bar(value, max) { return Math.min(100, Math.round(Number(value || 0) / max * 100)) + '%' },
    async load() {
      this.overview = await getChannelOverview()
      this.desk = this.overview.partner && this.overview.partner.levelCode === 'ECO' ? await getEcoDesk() : {}
      const partners = await getChannelPartners()
      this.downline = (partners.list || []).filter(item => item.id !== this.overview.partnerId)
      const settlements = await getChannelSettlements()
      this.settlements = settlements.list || []
      const vouchers = await getChannelVouchers()
      this.vouchers = vouchers.list || []
      const lines = await getChannelLines()
      this.lines = lines.list || []
      this.$nextTick(this.draw)
    },
    months() {
      const list = []
      const now = new Date()
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const key = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0')
        list.push({ key, label: (date.getMonth() + 1) + '月' })
      }
      return list
    },
    sumBy(rows, keyOf, valueOf) {
      return this.months().map(month => {
        return rows
          .filter(row => String(keyOf(row) || '').indexOf(month.key) === 0)
          .reduce((sum, row) => sum + Number(valueOf(row) || 0), 0) / 10000
      })
    },
    draw() {
      if (!this.$refs.chart) return
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chart)
        window.addEventListener('resize', this.resize)
      }
      const months = this.months()
      const legend = this.eco ? ['采购额', '消耗额'] : ['采购额', '消耗额', '分账收益']
      const series = [
        { name: '采购额', type: 'bar', barMaxWidth: 16, data: this.sumBy(this.vouchers.filter(item => item.source === 'PURCHASE'), row => row.createdAt, row => row.faceValue) },
        { name: '消耗额', type: 'bar', barMaxWidth: 16, data: this.sumBy(this.lines, row => row.createdAt, row => Number(row.points || 0) / 10) }
      ]
      if (!this.eco) {
        series.push({ name: '分账收益', type: 'line', smooth: true, symbol: 'circle', data: this.sumBy(this.lines, row => row.createdAt, row => row.amount) })
      }
      this.chart.setOption({
        color: ['#5b7cfa', '#9aa8c7', '#e2b340'],
        tooltip: { trigger: 'axis' },
        legend: { right: 0, top: 0, data: legend, textStyle: { color: '#667085', fontSize: 12 } },
        grid: { left: 36, right: 12, top: 36, bottom: 28 },
        xAxis: { type: 'category', data: months.map(item => item.label), axisLine: { lineStyle: { color: '#e6eaf2' } }, axisLabel: { color: '#98a2b3' } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef1f6' } }, axisLabel: { color: '#98a2b3' } },
        series
      }, true)
    },
    resize() { if (this.chart) this.chart.resize() },
    copy() { this.copyText(this.partner.inviteCode, '邀请码已复制') },
    copyLink() { this.copyText(this.promoUrl, '推广链接已复制') },
    copyText(value, success) {
      const input = document.createElement('textarea')
      input.value = value
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success(success)
    }
  }
}
</script>

<style scoped>
.board { max-width: 1280px; }
.head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 16px; }
h1 { margin: 0 0 6px; font-size: 22px; font-weight: 700; }
.head p, .invite p, .metrics p, .prog p, .tip { margin: 0; color: #667085; font-size: 12px; line-height: 1.6; }
.actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.pill, .gold, .ghost, .invite-row button {
  border-radius: 8px;
  height: 34px;
  padding: 0 14px;
  font-size: 13px;
  cursor: pointer;
}
.pill { border: 1px solid #f3d19c; background: #fff7e8; color: #b76e00; }
.gold { border: 0; background: #f0b429; color: #1e2a78; font-weight: 700; }
.ghost { border: 1px solid #c9d4f5; background: #fff; color: #1e2a78; }
.invite {
  display: grid;
  grid-template-columns: 1.4fr .8fr;
  gap: 16px;
  background: #fff;
  border: 1px solid #e7ebf3;
  border-radius: 12px;
  padding: 18px 20px;
}
.invite aside {
  background: #f7f8fc;
  border-radius: 8px;
  padding: 12px 14px;
  color: #667085;
  font-size: 12px;
  line-height: 1.7;
}
.invite-label { color: #98a2b3; font-size: 12px; }
.invite-row { display: flex; align-items: center; gap: 12px; margin: 8px 0; }
.discount {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #eef2ff;
  color: #1e2a78;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.invite-row strong { font-size: 26px; letter-spacing: 1px; }
.invite-row button { border: 0; background: #1e2a78; color: #fff; }
.metrics { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin: 12px 0; }
.metrics article, .panel {
  background: #fff;
  border: 1px solid #e7ebf3;
  border-radius: 12px;
  padding: 16px;
}
.invite-link { display: flex; align-items: center; gap: 8px; margin-top: 8px; color: #667085; font-size: 12px; }
.invite-link code { flex: 1; min-width: 0; word-break: break-all; background: #f7f8fc; border-radius: 6px; padding: 6px 8px; color: #1d2939; }
.invite-link button { border: 0; background: #1e2a78; color: #fff; border-radius: 8px; height: 30px; padding: 0 12px; cursor: pointer; }
.k { color: #667085; font-size: 13px; }
.v { margin: 8px 0 6px; font-size: 22px; font-weight: 700; color: #1d2939; }
.v small { font-size: 13px; font-weight: 500; color: #667085; }
.split { display: grid; grid-template-columns: .9fr 1.3fr; gap: 12px; }
h2 { margin: 0 0 14px; font-size: 15px; }
.prog { margin-bottom: 14px; }
.prog-top { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 6px; }
.bar { height: 8px; background: #eef1f6; border-radius: 4px; overflow: hidden; }
.bar i { display: block; height: 100%; background: #1e2a78; border-radius: 4px; }
.tip { margin-top: 8px; background: #fff8e8; color: #8a6116; border-radius: 8px; padding: 10px 12px; }
.chart-head { display: flex; justify-content: space-between; align-items: center; }
.chart-head span { color: #98a2b3; font-size: 12px; }
.chart { height: 280px; }
@media (max-width: 1100px) {
  .metrics, .split, .invite { grid-template-columns: 1fr; }
}
</style>
